import { config } from '../config/env';

type MessageHandler = (data: any) => void;

interface WebSocketMessage {
  type: string;
  sessionId?: string;
  userId?: string;
  data?: any;
  timestamp: string;
}

// Connection status states
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

// Status change event data
export interface ConnectionStatusEvent {
  status: ConnectionStatus;
  attempt?: number;
  maxAttempts?: number;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Increased from 5
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  private token: string | null = null;
  private currentSessionId: string | null = null;
  private eventHandlers: Map<string, Set<MessageHandler>> = new Map();
  private isManualDisconnect = false;
  private lastMessageTime: number = Date.now();
  private connectionStatus: ConnectionStatus = 'disconnected';

  // Heartbeat configuration (should align with backend pongWait of 60s)
  private readonly HEARTBEAT_INTERVAL = 30000; // Send ping every 30s
  private readonly HEARTBEAT_TIMEOUT = 10000; // Wait 10s for pong response
  private readonly RECONNECT_BASE_DELAY = 1000; // Start with 1s delay
  private readonly RECONNECT_MAX_DELAY = 30000; // Max 30s delay (increased from 5s)

  connect(token: string, sessionId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (this.currentSessionId !== sessionId) {
        this.leaveSession(this.currentSessionId!);
        this.joinSession(sessionId);
      }
      return;
    }

    this.token = token;
    this.currentSessionId = sessionId;
    this.isManualDisconnect = false;
    this.updateConnectionStatus('connecting');

    // Build WebSocket URL with auth token in query params
    // Normalize URL: strip trailing /api if present (for consistency with different VITE_API_URL formats)
    const normalizedWsUrl = config.wsUrl.replace(/\/+$/, '').replace(/\/api$/, '');
    const wsUrl = `${normalizedWsUrl}/api/ws/retrospectives/${sessionId}?token=${encodeURIComponent(token)}`;
    
    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.lastMessageTime = Date.now();
        this.updateConnectionStatus('connected');
        
        // Start heartbeat monitoring
        this.startHeartbeat();
        
        // Join the session (with lastSeen for reconnection sync)
        this.joinSession(sessionId);
      };

      this.ws.onmessage = (event) => {
        this.lastMessageTime = Date.now();
        
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          
          // Handle pong response (reset heartbeat timeout)
          if (message.type === 'pong') {
            this.resetHeartbeatTimeout();
            return;
          }
          
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emitStatusEvent('error', { error });
      };

      this.ws.onclose = (event) => {
        console.log(`WebSocket disconnected (code: ${event.code}, reason: ${event.reason || 'none'})`);
        this.cleanup();


        // Attempt reconnection if not manually disconnected
        if (!this.isManualDisconnect) {
          this.attemptReconnection();
        } else {
          this.updateConnectionStatus('disconnected');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.updateConnectionStatus('failed');
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Check if we've received any message recently
        const timeSinceLastMessage = Date.now() - this.lastMessageTime;
        
        if (timeSinceLastMessage > this.HEARTBEAT_INTERVAL * 2) {
          // No activity for too long, connection might be stale
          console.warn('Connection appears stale, forcing reconnection');
          this.forceReconnect();
          return;
        }
        
        // Send ping
        this.sendMessage({
          type: 'ping',
          timestamp: new Date().toISOString(),
        });
        
        // Set timeout for pong response
        this.heartbeatTimeout = setTimeout(() => {
          console.warn('Heartbeat timeout - no pong received');
          this.forceReconnect();
        }, this.HEARTBEAT_TIMEOUT);
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.resetHeartbeatTimeout();
  }

  private resetHeartbeatTimeout(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  private forceReconnect(): void {
    this.cleanup();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.attemptReconnection();
  }

  private attemptReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.updateConnectionStatus('failed');
      this.emitStatusEvent('max_retries_reached', {
        attempts: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
      });
      return;
    }

    this.reconnectAttempts++;
    
    // Exponential backoff with jitter
    const baseDelay = Math.min(
      this.RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts - 1),
      this.RECONNECT_MAX_DELAY
    );
    const jitter = Math.random() * 1000; // Add up to 1s of random jitter
    const delay = baseDelay + jitter;
    
    console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    this.updateConnectionStatus('reconnecting');
    
    this.reconnectTimeout = setTimeout(() => {
      if (this.token && this.currentSessionId) {
        this.connect(this.token, this.currentSessionId);
      }
    }, delay);
  }

  private cleanup(): void {
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private updateConnectionStatus(status: ConnectionStatus): void {
    const previousStatus = this.connectionStatus;
    this.connectionStatus = status;
    
    if (previousStatus !== status) {
      const event: ConnectionStatusEvent = {
        status,
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
      };
      
      // Emit connection status change event
      const handlers = this.eventHandlers.get('connection_status');
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(event);
          } catch (error) {
            console.error('Error in connection status handler:', error);
          }
        });
      }
    }
  }

  private emitStatusEvent(eventType: string, data: any): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${eventType} handler:`, error);
        }
      });
    }
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.cleanup();

    if (this.currentSessionId) {
      this.leaveSession(this.currentSessionId);
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.token = null;
    this.currentSessionId = null;
    this.reconnectAttempts = 0;
    this.updateConnectionStatus('disconnected');
  }

  // Reset reconnection counter (useful after manual reconnect succeeds)
  resetReconnectionAttempts(): void {
    this.reconnectAttempts = 0;
    if (this.connectionStatus === 'failed') {
      this.updateConnectionStatus('disconnected');
    }
  }

  // Manual reconnect (for "try again" buttons)
  manualReconnect(): void {
    if (this.token && this.currentSessionId) {
      this.reconnectAttempts = 0;
      this.isManualDisconnect = false;
      this.connect(this.token, this.currentSessionId);
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  // Join a retrospective session room
  joinSession(sessionId: string): void {
    this.sendMessage({
      type: 'join_session',
      sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  // Leave a retrospective session room
  leaveSession(sessionId: string): void {
    this.sendMessage({
      type: 'leave_session',
      sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  // Subscribe to events (including 'connection_status' for status changes)
  on(event: string, callback: MessageHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  // Unsubscribe from events
  off(event: string, callback?: MessageHandler): void {
    if (!callback) {
      this.eventHandlers.delete(event);
    } else {
      this.eventHandlers.get(event)?.delete(callback);
    }
  }

  // Send a message through WebSocket
  private sendMessage(message: Partial<WebSocketMessage>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected, cannot send message');
    }
  }

  // Handle incoming messages
  private handleMessage(message: WebSocketMessage): void {
    const handlers = this.eventHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message.data || message);
        } catch (error) {
          console.error(`Error in handler for ${message.type}:`, error);
        }
      });
    }
  }

  // Get current socket (for compatibility)
  getSocket(): WebSocket | null {
    return this.ws;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
